import { BET_SUBMISSION } from '@constants/bettingConstants'

/**
 * WHEN a pending bet is allowed to reach the server.
 *
 * Betting is DEFERRED: pressing Bet only stores the bet in frontend state, and
 * the PLACE_BET emit is withheld until `BET_SUBMISSION.LEAD_MS` before the round
 * takes off — the last moment the server still accepts bets. Until then the bet
 * is fully the player's: cancelable, re-stakeable, replaceable.
 *
 * This module is the single home for that deadline. It deliberately imports no
 * orchestrator, so both orchestrators can ask "may I emit now?" without an import
 * cycle — the scheduler owns the timer and is the SINGLE WRITER here.
 */

// Armed for the CURRENT ACCEPT_BET window only. `deadlineAt` is the epoch ms at
// which the server stops accepting bets; `null` while armed means the server did
// not tell us when, in which case submission counts as due immediately rather
// than silently swallowing the bet.
let armed = false
let deadlineAt = null

// Resolve the close-of-betting instant from an ACCEPT_BET tick. The server sends
// `acceptBetEndTime` (epoch ms); `delay` (ms remaining in the window) is the
// fallback, valid only because we resolve it as the tick arrives.
const resolveDeadline = ({ acceptBetEndTime, delay } = {}, now) => {
  const end = Number(acceptBetEndTime)
  if (Number.isFinite(end) && end > 0) return end
  const remaining = Number(delay)
  if (Number.isFinite(remaining) && remaining > 0) return now + remaining
  return null
}

// Open the window for this round. Returns the resolved deadline (or null).
export const armSubmissionWindow = (data, now = Date.now()) => {
  armed = true
  deadlineAt = resolveDeadline(data, now)
  console.log('BETDEBUG-4: submission window armed, deadline resolved', {
    data,
    now,
    nowISO: new Date(now).toISOString(),
    resolvedDeadlineAt: deadlineAt,
    deadlineISO: deadlineAt != null ? new Date(deadlineAt).toISOString() : null,
  })
  return deadlineAt
}

// Close the window — called on every non-ACCEPT_BET phase and on socket
// teardown, so a stale deadline can never authorise an emit into a live round.
export const disarmSubmissionWindow = () => {
  console.log('BETDEBUG-context: submission window disarmed')
  armed = false
  deadlineAt = null
}

// How long to wait before pending bets should be sent.
//   null -> no window armed, nothing to schedule
//   0    -> send now
export const msUntilSubmission = (now = Date.now()) => {
  if (!armed) return null
  if (deadlineAt == null) return 0
  return Math.max(0, deadlineAt - BET_SUBMISSION.LEAD_MS - now)
}

// The gate every emit path checks. True once we are inside the final LEAD_MS of
// the betting window — and it STAYS true for the rest of the window, so a bet
// placed after the scheduled fire is sent immediately instead of waiting a round.
export const isSubmissionDue = (now = Date.now()) => {
  if (!armed) {
    console.log('BETDEBUG-context: isSubmissionDue -> false (window not armed)')
    return false
  }
  if (deadlineAt == null) return true
  const due = deadlineAt - BET_SUBMISSION.LEAD_MS <= now
  console.log('BETDEBUG-context: isSubmissionDue check', {
    now,
    deadlineAt,
    leadMs: BET_SUBMISSION.LEAD_MS,
    msUntilDue: deadlineAt - BET_SUBMISSION.LEAD_MS - now,
    due,
  })
  return due
}
